import {ChangeEvent, FC, Fragment, ReactNode, useEffect, useRef, useState} from 'react';
import {useRecoilValue, useRecoilValueLoadable, useSetRecoilState} from 'recoil';
import {PhotoView} from 'react-photo-view';
import {useFormContext} from 'react-hook-form';
import {toast} from 'react-toastify';
import {get, omit} from 'lodash';

import {Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography, Theme, SxProps} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VideocamIcon from '@mui/icons-material/Videocam';

import {useFileUpload} from '../../../../hooks/useFileUpload';

import {IDocument} from '../../../../shared/types/document.types';
import {ISelectedDocToReplace} from './utils';

import {colors} from '../../../../shared/colors';

import {contentTreeFilesQueryFamily} from '../../../../store/service-requests/selectors';
import {
  contentTreeQueryTrigger,
  docsToReplaceState,
  serviceRequestActiveTabState,
} from '../../../../store/service-requests/atoms';

import splash from '../../../../assets/Subtract.jpg';
import {InputTypeEnum} from '../../../../shared/types/workContent.types';
import {ModalContainer} from '../../../modals/ModalContainer';
import {ConfirmAction} from '../../../modals/ConfirmAction';
import {useEventBus} from '../../../../hooks/useEventBus';
import {ServiceRequestService} from '../../../../api/services/serviceRequest.service';
import {useParams} from 'react-router-dom';
import {DateHelpers} from '../../../../shared/helpers/dateHelpers';
import {WorkDataInformationEdit} from './WorkDataInformationEdit';
import {useCompanyTariffGuard, useRefreshCompanyTariff} from '../../../../modules/company-tariff';

interface IWorkDataMedia {
  item: {
    files?: IDocument[];
    text: string;
    formName: string;
    mandatory?: boolean;
    maxFilesAllowed?: number;
  };
  isEditable: boolean;
  isTextVisible?: boolean;
  mediaType: InputTypeEnum.IMAGE | InputTypeEnum.VIDEO;
  isMultipleInput?: boolean;
}

const getMoreThanAllowedText = (text: string, maxFiles: number) =>
  `Количество выбранных/загруженных файлов больше допустимого значения. Максимальное значение для ${text}: ${maxFiles}`;

const RenderVideo: FC<{url: string}> = ({url}) => (
  <Stack>
    <video style={{height: 'calc(100vh - 50px)'}} controls src={url} />
  </Stack>
);

const getIsVideo = (mimeType: string) => {
  if (!mimeType) {
    return false;
  }

  const generalType = mimeType.split('/')[0].toUpperCase();

  return InputTypeEnum.VIDEO === generalType;
};

const getIsImage = (mimeType: string) => {
  if (!mimeType) {
    return false;
  }

  const generalType = mimeType.split('/')[0].toUpperCase();

  return InputTypeEnum.IMAGE === generalType;
};

export const WorkDataMedia: FC<IWorkDataMedia> = ({
  item,
  isEditable,
  isTextVisible = true,
  mediaType,
  isMultipleInput = false,
}) => {
  const isVideo = mediaType === InputTypeEnum.VIDEO;
  const prevFiles = item.files ? item.files.filter((i) => (isVideo ? getIsVideo : getIsImage)(i.mimeType)) : [];

  const {id} = useParams();
  const activeTab = useRecoilValue(serviceRequestActiveTabState);
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const refreshCompanyTariff = useRefreshCompanyTariff();
  const {contents, state} = useRecoilValueLoadable(
    contentTreeFilesQueryFamily(prevFiles.length ? prevFiles.map((photo) => photo.id) : null),
  );
  const setDocsToReplace = useSetRecoilState(docsToReplaceState);
  const inputRef = useRef<HTMLInputElement>(null);
  const {files, fileNames, setFiles, clearAllFiles, removeFile} = useFileUpload();
  const {files: filesToReplace, setFiles: setFilesToReplace, clearAllFiles: clearAllFilesToReplace} = useFileUpload();
  const bus = useEventBus();
  const [selectedToReplace, setSelectedToReplace] = useState<ISelectedDocToReplace | null>(null);
  const [replaceMode, setReplaceMode] = useState(false);
  const [open, setOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const triggerContentTreeQuery = useSetRecoilState(contentTreeQueryTrigger);
  const isOnlyUploadMode = Boolean(activeTab?.onlyUpload);
  const isEditMode = isEditable && activeTab?.editMode;
  const canModifyExistingFiles = isEditMode && !isOnlyUploadMode;

  const handleFileSelect = () => {
    if (!checkTariff(true)) return;

    inputRef.current?.click();
  };

  const closeModal = () => {
    setOpen(false);
    setIdToDelete(null);
  };

  const deleteHandler = async (setLoading: (arg: boolean) => void) => {
    if (!idToDelete || !id) {
      return;
    }

    try {
      setLoading(true);
      await ServiceRequestService.deleteDocument(Number(id), idToDelete);
      refreshCompanyTariff();
      triggerContentTreeQuery((v) => v + 1);
      closeModal();
    } catch (err) {
      bus?.emit('error', [err]);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelected = (url: string, index: number) => {
    if (!prevFiles || !prevFiles?.length || !canModifyExistingFiles) {
      return;
    }

    setSelectedToReplace((prev) =>
      prev?.oldSrc === url ? prev : {oldSrc: url, newSrc: null, docId: prevFiles[index].id},
    );
  };

  const {setValue, getValues} = useFormContext();

  const defaultValueRef = useRef(getValues(item.formName));

  const isFilesExist = state === 'hasValue' && Array.isArray(contents) && !!contents.length;

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget?.files && !replaceMode) {
      const filesFromEvent = e.currentTarget?.files || [];
      const prevFiles = getValues(item.formName) as string | File[];

      const filesArr = Array.from(filesFromEvent);
      const downloadedFilesLength = item?.files?.length ?? 0;
      const formFilesLength = isMultipleInput && Array.isArray(prevFiles) ? prevFiles.length : 0;

      const isMoreThanAllowed = item.maxFilesAllowed
        ? filesArr.length > item.maxFilesAllowed ||
          filesArr.length + downloadedFilesLength > item.maxFilesAllowed ||
          filesArr.length + downloadedFilesLength + formFilesLength > item.maxFilesAllowed
        : false;

      if (isMoreThanAllowed) {
        toast.error(getMoreThanAllowedText(item.text, item.maxFilesAllowed as number), {containerId: 'default'});

        return;
      }
    }

    if (replaceMode) {
      if (selectedToReplace && e.currentTarget?.files?.length) {
        setSelectedToReplace((prev) =>
          prev ? {...prev, newSrc: URL.createObjectURL((e.currentTarget?.files as FileList)[0])} : prev,
        );
      }

      setFilesToReplace(e);
    } else {
      setFiles(e);
    }
  };

  const renderNonEditForm = () =>
    state === 'loading' ? (
      <Stack sx={{height: '50px', width: '50px', alignItems: 'center', justifyContent: 'center'}}>
        <CircularProgress size="small" sx={{height: '22px', width: '22px'}} />
      </Stack>
    ) : state === 'hasValue' && Array.isArray(contents) ? (
      contents.map((url, ind) =>
        url ? (
          <PhotoView
            key={url + ind}
            overlay={
              <Typography color="#fff" variant="h6">
                {item.text}
              </Typography>
            }
            src={isVideo ? '' : url}
            render={() => <RenderVideo url={url} />}
          >
            <Tooltip
              title={
                <Fragment>
                  <Typography sx={{fontSize: '12px'}} textOverflow="ellipsis">
                    <b>Дата создания:</b> {DateHelpers.formatToRu(get(prevFiles, `${ind}.createdDate`))}
                  </Typography>
                  <Typography sx={{fontSize: '12px'}} textOverflow="ellipsis">
                    <b>Автор:</b> {get(prevFiles, `${ind}.createdFio`) || '-'}
                  </Typography>
                  <Typography sx={{fontSize: '12px'}} textOverflow="ellipsis">
                    <b>Комментарий:</b> {get(prevFiles, `${ind}.description`) || '-'}
                  </Typography>
                </Fragment>
              }
            >
              {isVideo ? (
                <Box
                  data-url={url}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '49px',
                    width: '49px',
                    border: (t: Theme) => `1px solid ${t.palette.primary.main}`,
                    cursor: 'pointer',
                  }}
                >
                  <PlayArrowIcon sx={{fontSize: '20px'}} />
                </Box>
              ) : (
                <img style={{height: '50px', width: '50px', cursor: 'pointer'}} src={url} alt={item.text + ind} />
              )}
            </Tooltip>
          </PhotoView>
        ) : (
          <Tooltip title="Ошибка загрузки" key={url + ind}>
            <img
              style={{height: '49px', width: '49px', border: `1px solid ${colors['red']}`}}
              src={splash}
              alt="Ошибка загрузки"
            />
          </Tooltip>
        ),
      )
    ) : (
      <Tooltip title={`${isVideo ? 'Видео' : 'Фото'} не было загружено (необязательное)`}>
        <img style={{height: '50px', width: '50px'}} src={splash} alt="Необязательное" />
      </Tooltip>
    );

  const renderEditForm = () => {
    let arrDownloadedFiles: ReactNode[] = [];
    let arrLocalFiles: ReactNode[] = [];

    if (state === 'loading') {
      return (
        <Stack sx={{height: '50px', width: '50px', alignItems: 'center', justifyContent: 'center'}}>
          <CircularProgress size="small" sx={{height: '22px', width: '22px'}} />
        </Stack>
      );
    }

    if (files.length) {
      arrLocalFiles = arrLocalFiles.concat(
        files.map((file, ind) => {
          const url = URL.createObjectURL(file);
          const boxSx: SxProps<Theme> = isVideo
            ? {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '49px',
                width: '49px',
                border: (t: Theme) => `1px solid ${t.palette.primary.main}`,
              }
            : {
                height: '50px',
                width: '50px',
              };

          return (
            <PhotoView
              triggers={['onDoubleClick']}
              key={file.lastModified}
              overlay={
                <Typography color="#fff" variant="h6">
                  {item.text}
                </Typography>
              }
              src={isVideo ? '' : url}
              render={() => <RenderVideo url={url} />}
            >
              <Box data-url={url} sx={{...boxSx, cursor: 'pointer', position: 'relative'}}>
                {isVideo ? (
                  <VideocamIcon sx={{fontSize: '20px'}} />
                ) : (
                  <img style={{height: '50px', width: '50px', cursor: 'pointer'}} src={url} alt={fileNames[ind]} />
                )}
                <Stack direction="row" position="absolute" top={0} left={0} right={0} justifyContent="space-between">
                  {canModifyExistingFiles && <WorkDataInformationEdit document={prevFiles[ind]} isPhoto={!isVideo} />}
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(file.name);
                    }}
                    sx={{width: 20, height: 20}}
                  >
                    <HighlightOffIcon htmlColor={isVideo ? '#818181' : '#fff'} />
                  </IconButton>
                </Stack>
              </Box>
            </PhotoView>
          );
        }),
      );
    }

    if (state === 'hasValue' && Array.isArray(contents)) {
      arrDownloadedFiles = arrDownloadedFiles.concat(
        contents.map((url, ind) => {
          const urlForClickHandler = url || (prevFiles?.[ind]?.link as string);
          const selected = selectedToReplace?.oldSrc === urlForClickHandler;

          const src = (selected && selectedToReplace?.newSrc) || url;

          const clickHandler = () => {
            if (replaceMode) {
              toggleSelected(urlForClickHandler, ind);
            }
          };

          const boxSx: SxProps<Theme> = isVideo
            ? {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '49px',
                width: '49px',
                border: (t: Theme) => `1px solid ${t.palette.primary.main}`,
                cursor: 'pointer',
              }
            : {
                height: '50px',
                width: '50px',
              };

          return src || selected ? (
            <Box
              key={url + ind}
              sx={{
                ...(selected
                  ? {
                      ...boxSx,
                      border: '2px solid blue',
                      height: '54px',
                    }
                  : boxSx),
                position: 'relative',
              }}
              onClick={clickHandler}
            >
              <PhotoView
                triggers={['onDoubleClick']}
                key={url + ind}
                overlay={
                  <Typography color="#fff" variant="h6">
                    {item.text}
                  </Typography>
                }
                src={isVideo ? '' : src}
                render={() => <RenderVideo url={src} />}
              >
                <>
                  {isVideo ? (
                    <Box sx={boxSx} data-url={src}>
                      <VideocamIcon sx={{fontSize: '20px'}} />
                    </Box>
                  ) : (
                    <img
                      style={{height: '50px', width: '50px', cursor: 'pointer'}}
                      src={src || splash}
                      alt="Ошибка загрузки"
                    />
                  )}
                  {canModifyExistingFiles && (
                    <Stack
                      direction="row"
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      justifyContent="space-between"
                    >
                      <WorkDataInformationEdit document={prevFiles[ind]} isPhoto={!isVideo} />
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setIdToDelete(prevFiles?.[ind]?.id as string);
                          setOpen(true);
                        }}
                        sx={{width: 20, height: 20}}
                      >
                        <HighlightOffIcon htmlColor={isVideo ? '#818181' : '#fff'} />
                      </IconButton>
                    </Stack>
                  )}
                </>
              </PhotoView>
            </Box>
          ) : (
            <Tooltip key={url + ind} title="Ошибка загрузки">
              <img
                style={{height: '49px', width: '49px', border: `1px solid ${colors['red']}`}}
                src={splash}
                alt="Ошибка загрузки"
                onClick={clickHandler}
              />
            </Tooltip>
          );
        }),
      );
    }

    const showSplash = !arrDownloadedFiles.length && !arrLocalFiles.length;

    return (
      <>
        {showSplash && (
          <img
            style={{height: '49px', width: '49px', border: item.mandatory ? `1px solid ${colors['red']}` : undefined}}
            src={splash}
            alt="splash"
          />
        )}
        {arrDownloadedFiles}
        {arrLocalFiles}
      </>
    );
  };

  useEffect(() => {
    if (!canModifyExistingFiles && replaceMode) {
      setDocsToReplace((prev) =>
        selectedToReplace?.docId ? prev.filter((doc) => doc.docId !== selectedToReplace.docId) : prev,
      );
      setSelectedToReplace(null);
      setReplaceMode(false);
      clearAllFilesToReplace();
    }
  }, [canModifyExistingFiles, replaceMode, clearAllFilesToReplace, selectedToReplace, setDocsToReplace]);

  useEffect(() => {
    if (selectedToReplace && filesToReplace.length) {
      setDocsToReplace((prev) =>
        prev
          .filter((i) => i.docId !== selectedToReplace.docId)
          .concat({...omit(selectedToReplace, ['newSrc']), file: filesToReplace[0]}),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToReplace?.newSrc]);

  useEffect(() => {
    if (selectedToReplace?.oldSrc) {
      clearAllFilesToReplace();
      setDocsToReplace((prev) => prev.filter((i) => i.docId !== selectedToReplace.docId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedToReplace?.oldSrc]);

  useEffect(() => {
    if (!activeTab?.editMode) {
      clearAllFiles();
      clearAllFilesToReplace();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab?.editMode]);

  useEffect(() => {
    const syncSelectedFilesToForm = (selectedFiles: File[], needToConcat = true) => {
      const prevFiles = getValues(item.formName) as File[];

      const newValue =
        isMultipleInput && Array.isArray(prevFiles)
          ? Array.from(
              new Set(
                prevFiles
                  .filter((i) => !selectedFiles.find((file) => file.name === i.name))
                  .concat(needToConcat ? selectedFiles : []),
              ),
            )
          : selectedFiles.length
            ? selectedFiles
            : defaultValueRef.current;

      setValue(item.formName, newValue, {shouldDirty: true});
    };

    syncSelectedFilesToForm(files);

    return () => {
      syncSelectedFilesToForm(files, false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  useEffect(() => {
    if (state === 'hasError') bus?.emit('error', [contents, {toastId: 'contentTreePhotoQuery'}]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <Stack gap={2} sx={{flexWrap: 'wrap'}} direction="row" alignItems="center">
      {!isEditMode ? renderNonEditForm() : renderEditForm()}
      <Stack direction="row">
        {item.mandatory ? (
          <Typography variant="subtitle2" color="red">
            *
          </Typography>
        ) : null}
        {isTextVisible && (
          <Typography
            sx={{
              color: state === 'hasError' ? colors['red'] : colors['icons--primary'],
              width: 'fit-content',
              maxWidth: activeTab?.editMode ? 290 : 320,
            }}
            textOverflow="ellipsis"
            noWrap
            variant="subtitle2"
          >
            {item.text}
          </Typography>
        )}
      </Stack>
      {isEditMode && !replaceMode ? (
        <Tooltip title={!replaceMode ? 'Выбрать для загрузки новое фото' : 'Выбрать для загрузки фото'}>
          <IconButton onClick={handleFileSelect} disabled={tariffLoading} aria-label="Загрузить документы" size="small">
            <UploadIcon />
          </IconButton>
        </Tooltip>
      ) : null}
      {canModifyExistingFiles && isFilesExist ? (
        <Stack spacing={1} direction="row">
          <Button
            onClick={() => {
              if (!replaceMode) {
                setReplaceMode(true);
              } else {
                handleFileSelect();
              }
            }}
            disabled={tariffLoading || (replaceMode && !selectedToReplace)}
          >
            {!replaceMode ? `Замена ${isVideo ? 'видео' : 'фотографии'}` : 'Выбрать'}
          </Button>
          {replaceMode && (
            <Button
              onClick={() => {
                setSelectedToReplace(null);
                setReplaceMode(false);
                clearAllFilesToReplace();
              }}
            >
              Отмена
            </Button>
          )}
        </Stack>
      ) : null}
      {isEditMode ? (
        <input
          type="file"
          ref={inputRef}
          style={{display: 'none'}}
          onChange={onChange}
          multiple={!replaceMode}
          accept={isVideo ? 'video/*' : 'image/*'}
        />
      ) : null}
      {open && (
        <ModalContainer
          sideEffect={deleteHandler}
          renderModal={(sideEffect, loading) => (
            <ConfirmAction
              loading={loading}
              open={open}
              handleClose={closeModal}
              handleSideEffect={sideEffect}
              title="Вы уверены? После данного действия файл будет удален навсегда"
              buttonTitle="Удалить"
            />
          )}
        />
      )}
    </Stack>
  );
};
